// M2R.5 — L2OtpModal: NV verify TOTP L2 trên máy kiosk.
// Props:
//   - open: bool
//   - employee: { id, full_name, phone, ... } — NV đang verify (BẮT BUỘC)
//   - onClose(): user Huỷ / ESC
//   - onSuccess(employee): verify OK — parent chạy tiếp action
//   - actionLabel: optional label hiển thị ("Bắt đầu học", "Đăng nhập", ...)
//
// Verify success → setL2Session + parent onSuccess.
// AC-AUTH-6: sai ≥5 → 423 + Telegram alert admin; dưới 5 thì hiện "còn N lần".
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { api } from '../lib/api.js';
import { useAuthStore } from '../lib/auth.js';
import TotpInput from './TotpInput.jsx';

export default function L2OtpModal({
  open, employee, onClose, onSuccess,
  actionLabel = 'Xác nhận thao tác',
}) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [attemptsLeft, setAttemptsLeft] = useState(null);
  const [locked, setLocked] = useState(null); // { until }
  const setL2Session = useAuthStore(s => s.setL2Session);

  useEffect(() => {
    if (open) {
      setCode('');
      setAttemptsLeft(null);
      setLocked(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open || !employee) return null;

  async function submit(e, codeOverride) {
    e?.preventDefault?.();
    const c = codeOverride ?? code;
    if (c.length !== 6 || busy || locked) return;
    setBusy(true);
    try {
      const res = await api.totpL2Verify(employee.id, c);
      setL2Session({
        sessionId: res.l2_session,
        expiresAt: res.expires_at,
        employee: res.employee,
      });
      toast.success(`✓ Đã xác minh — chào ${res.employee.full_name}, phiên 4 giờ`);
      onSuccess?.(res.employee);
    } catch (err) {
      if (err.status === 423) {
        setLocked({ until: err.data?.locked_until });
        toast.error('NV đang bị khoá L2 — báo admin');
      } else if (err.status === 401) {
        const remaining = err.data?.attempts_remaining;
        setAttemptsLeft(typeof remaining === 'number' ? remaining : null);
        toast.error(`Mã sai${typeof remaining === 'number' ? ` — còn ${remaining} lần` : ''}`);
        setCode(''); // TotpInput reset lastFiredValue → cho phép auto-submit lại
      } else if (err.data?.code === 'L2_NOT_SETUP') {
        toast.error('NV chưa thiết lập L2 — dùng luồng thiết lập lần đầu');
        onClose?.();
      } else {
        toast.error(err.data?.error || err.message);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-800">🔐 Xác minh L2</h3>
          <p className="text-sm text-slate-500 mt-1">
            {actionLabel} — NV <span className="font-semibold">{employee.full_name}</span>
          </p>
          <p className="text-xs text-slate-400 mt-0.5">
            Nhập mã 6 số từ Google Authenticator của bạn
          </p>
        </div>

        <TotpInput
          value={code}
          onChange={setCode}
          onSubmit={(c) => submit(null, c)}
          autoFocus
          disabled={busy || !!locked}
          placeholder="000000"
          testId="l2-totp-input"
        />

        {attemptsLeft != null && !locked && (
          <div className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 border border-amber-200">
            Sai mã — còn <span className="font-semibold">{attemptsLeft}</span> lần trước khi khoá.
          </div>
        )}
        {locked && (
          <div className="text-xs text-rose-700 bg-rose-50 rounded-lg px-3 py-2 border border-rose-200">
            NV bị khoá đến {locked.until ? new Date(locked.until).toLocaleTimeString('vi-VN') : 'sau 30 phút'}.
            Liên hệ admin để mở.
          </div>
        )}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium"
          >
            Huỷ
          </button>
          <button
            type="submit"
            disabled={busy || code.length !== 6 || !!locked}
            className="flex-1 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium disabled:opacity-50"
          >
            {busy ? 'Đang xác minh…' : 'Xác nhận'}
          </button>
        </div>
      </form>
    </div>
  );
}
